import { ROUTE_SEGMENT } from '@/constants/routeSegments';
import { SEARCH_PARAM } from '@/constants/searchParams';

/** Query param carrying serialized filter/sort state on public search pages. */
export const PUBLIC_SEARCH_STATE_PARAM = 'search';

function copyCatalogueContextParams(
  source: URLSearchParams | string | null | undefined,
): URLSearchParams {
  const params = new URLSearchParams(
    typeof source === 'string' ? source : (source?.toString() ?? ''),
  );
  params.delete(SEARCH_PARAM.TAB);
  params.delete(SEARCH_PARAM.SALE_ID);
  return params;
}

/**
 * Build animal detail href with sale catalogue context for lot browsing.
 */
export function buildSaleLotAnimalHref(
  orgCode: string,
  animalId: number | string,
  options: {
    saleId: string | number;
    lotNo: string;
    searchParams?: URLSearchParams | string | null;
  },
): string {
  const qs = buildCatalogueAnimalQueryString(
    options.saleId,
    options.lotNo,
    options.searchParams,
  );
  return `/${orgCode}/${ROUTE_SEGMENT.ANIMALS}/${animalId}${qs ? `?${qs}` : ''}`;
}

/** Lot identity remains sale + raw lot number when no animal record exists. */
export function buildSaleLotDetailHref(
  orgCode: string,
  saleId: string | number,
  lotNo: string,
  searchParams?: URLSearchParams | string | null,
): string {
  const params = copyCatalogueContextParams(searchParams);
  params.delete(SEARCH_PARAM.LOT_NO);
  const qs = params.toString();
  return `/${orgCode}/${ROUTE_SEGMENT.SALES}/${saleId}/lots/${encodeURIComponent(lotNo)}${qs ? `?${qs}` : ''}`;
}

/** Select the detail route without inventing an animal id for a sale-only lot. */
export function buildSaleLotHref(
  orgCode: string,
  options: {
    saleId: string | number;
    lotNo: string;
    animalId?: number | null;
    searchParams?: URLSearchParams | string | null;
    tab?: string | null;
  },
): string {
  const animalId = options.animalId;
  const href =
    animalId != null && Number.isInteger(animalId) && animalId > 0
      ? buildSaleLotAnimalHref(orgCode, animalId, options)
      : buildSaleLotDetailHref(
          orgCode,
          options.saleId,
          options.lotNo,
          options.searchParams,
        );
  if (!options.tab) return href;
  const [pathname, query] = href.split('?');
  const params = new URLSearchParams(query);
  params.set(SEARCH_PARAM.TAB, options.tab);
  return `${pathname}?${params.toString()}`;
}

/**
 * Build sale listing href, optionally anchoring to a lot via lotNo query param.
 */
export function buildSaleListingHref(
  orgCode: string,
  saleId: string | number,
  searchParams?: URLSearchParams | string | null,
  options?: { lotNo?: string },
): string {
  const params = copyCatalogueContextParams(searchParams);
  if (options?.lotNo) {
    params.set(SEARCH_PARAM.LOT_NO, options.lotNo);
  } else {
    params.delete(SEARCH_PARAM.LOT_NO);
  }
  const qs = params.toString();
  return `/${orgCode}/${ROUTE_SEGMENT.SALES}/${saleId}${qs ? `?${qs}` : ''}`;
}

/**
 * Params to preserve on the animal detail URL when paging between lots.
 */
function buildCatalogueAnimalQueryString(
  saleId: string | number,
  lotNo: string,
  searchParams?: URLSearchParams | string | null,
  tab?: string | null,
): string {
  const params = copyCatalogueContextParams(searchParams);
  params.set(SEARCH_PARAM.SALE_ID, String(saleId));
  params.set(SEARCH_PARAM.LOT_NO, lotNo);
  if (tab) {
    params.set(SEARCH_PARAM.TAB, tab);
  }
  return params.toString();
}
